import { useQuery } from "@apollo/client";
import { uniqBy } from "lodash";
import moment from "moment";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  GetProfileReviewsQueryQuery,
  GetProfileReviewsQueryQueryVariables,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import { assertEdgesNonNull, getUrlUsingTypename } from "@helpers/helpers";

import ProfileTabEmptyState from "../../components/profile-tab-empty-state";
import ProfileContainer from "../../profile-container";
import ProfileEmptyState from "../../profile-empty-state";
import getProfileReviewsQuery from "../graphql/get-profile-reviews-query";

const ProfileReviews = ({
  profileId,
  bare = false,
}: {
  profileId: number;
  bare?: boolean;
}) => {
  const location = useLocation<TFLocationState>();
  const label = useLabel();
  const { data, loading, fetchMore } = useQuery<
    GetProfileReviewsQueryQuery,
    GetProfileReviewsQueryQueryVariables
  >(getProfileReviewsQuery, {
    variables: { profileId },
  });

  const deliveries = useMemo(() => {
    const assessmentDeliveries = data?.assessmentDeliveries
      ? assertEdgesNonNull(data?.assessmentDeliveries)
      : [];

    return uniqBy(
      assessmentDeliveries,
      (delivery) => `${delivery.complianceProgram?.id}-${delivery.target?.id}`,
    );
  }, [data]);

  const handleClickLoadMore = () => {
    if (data?.assessmentDeliveries?.pageInfo?.hasNextPage) {
      fetchMore({
        variables: {
          after: data?.assessmentDeliveries?.pageInfo?.endCursor,
          merge: true,
        },
      });
    }
  };

  return (
    <>
      <ProfileContainer
        title={`${label("review", { pluralize: true, titleCase: true })}`}
        bare={bare}
        topRightContent={
          bare ? null : (
            <Button
              theme={buttonTheme.text}
              size={ButtonSize.medium}
              to={`/assessments/delivered?userId=${profileId}`}
            >
              View All {label("review", { pluralize: true, titleCase: true })}
            </Button>
          )
        }
      >
        {loading && !data ? (
          <Loading className="" />
        ) : deliveries.length > 0 ? (
          <div className="flex flex-col gap-5">
            {deliveries.map((delivery) => (
              <div key={delivery.id} className="flex justify-between gap-2">
                <AppLink
                  to={getUrlUsingTypename(delivery, location)}
                  className="text-blue-link hover:underline"
                >
                  {delivery.complianceProgram?.title}
                </AppLink>
                <span className="text-gray-500 tracking-tight shrink-0">
                  {delivery.deliveryDatetime
                    ? moment(delivery.deliveryDatetime).format("MMM Do, YYYY")
                    : delivery.lastSubmissionDatetime
                      ? moment(delivery.lastSubmissionDatetime).format(
                          "MMM Do, YYYY",
                        )
                      : ""}
                </span>
              </div>
            ))}
            {deliveries.length > 0 &&
              data?.assessmentDeliveries?.pageInfo?.hasNextPage && (
                <div className="flex justify-center gap-2">
                  <Button
                    size={ButtonSize.medium}
                    text={loading ? "loading..." : "load more"}
                    onClick={handleClickLoadMore}
                    disabled={loading}
                  />
                </div>
              )}
          </div>
        ) : bare ? (
          <ProfileTabEmptyState
            title={`No ${label("review", { titleCase: true, pluralize: true })} Yet`}
            body={`${label("review", { titleCase: true, pluralize: true })} will show up here once they're delivered.`}
          />
        ) : (
          <ProfileEmptyState>
            No {label("review", { pluralize: true })}.
          </ProfileEmptyState>
        )}
      </ProfileContainer>
    </>
  );
};

export default ProfileReviews;
